import { useEffect } from 'react';
import { isDesktopShell } from './bridge';
import { isDesktopCallWindow } from './callWindow';
import { installDesktopScreenShare } from './displayMedia';
import { requestScreenSource } from './screenSources';
import { ScreenSourcePicker } from './components/ScreenSourcePicker';
import { useDesktopAuthBridge } from './hooks/useDesktopAuthBridge';
import { useDesktopTitleBar } from './hooks/useDesktopTitleBar';
import { useDesktopCallActivity } from './hooks/useDesktopCallActivity';
import { useDesktopCallPresence } from './hooks/useDesktopCallPresence';
import { useDesktopCalls } from './hooks/useDesktopCalls';
import { useDesktopEvent } from './hooks/useDesktopEvent';
import { useDesktopHotkeys } from './hooks/useDesktopHotkeys';
import { useDesktopMediaState } from './hooks/useDesktopMediaState';
import { useDesktopNavigation } from './hooks/useDesktopNavigation';
import { useDesktopPresence } from './hooks/useDesktopPresence';
import { useDesktopUpdates } from './hooks/useDesktopUpdates';

/**
 * Everything the desktop shell adds to the app, mounted once per document.
 *
 * In a browser every hook here resolves to a no-op subscription and the picker
 * never opens, so this costs a browser session nothing but the import.
 *
 * TWO KINDS OF DOCUMENT NOW. A call runs in a window of its own, and that
 * window loads this same app — so the parts that belong to the WORKSPACE (the
 * tray's presence, the updater's toasts, deep links, the native ringer) must
 * not run in it. Most of them are already inert there, because `sendEvent` in
 * the main process addresses the workspace window and only the workspace
 * window; splitting them out anyway is what makes that a rule rather than a
 * coincidence one refactor away from breaking.
 *
 * Four things are common to both, and each for its own reason:
 *   • the screen-share patch and picker — sharing must work FROM the call
 *     window, which is the whole point of putting the call there;
 *   • the global mute hotkey — it acts on whichever document holds the live
 *     media session, and during a call that is the call window. (Global
 *     push-to-talk was removed; see apps/desktop/src/main/hotkeys.ts.);
 *   • the media-state report — the shell can only route those hotkeys to ONE
 *     document if every document tells it what its microphone is doing. Without
 *     this half the main process had to broadcast, which opened two
 *     microphones on one keypress whenever the workspace held media too;
 *   • the token bridge — one half of it publishes (workspace), the other half
 *     receives (call window), and it decides which by asking the shell.
 *
 * AND ONE THING IS SPLIT RATHER THAN SHARED OR OMITTED: presence. A person has
 * one presence and one heartbeat, so `PresenceRuntime` stays workspace-only —
 * but the call it should be reporting now happens in a different document, and
 * simply leaving it out meant the workspace showed the caller THEIR OWN chip
 * and tray as Away five minutes into every call. Only their own: the roster
 * always read "In a call", which the server derives by trigger from
 * `call_participants`. So the call window publishes what it is doing
 * (`useDesktopCallActivity`) and the workspace folds the shell's aggregate into
 * its own flags (`useDesktopCallPresence`).
 */
export function DesktopRuntime() {
  useEffect(() => {
    installDesktopScreenShare();
  }, []);

  useDesktopHotkeys();
  useDesktopMediaState();
  useDesktopAuthBridge();
  useDesktopTitleBar();

  /* The main process reached Electron's display-media handler without going
     through our patched `getDisplayMedia` — show the same picker and answer it.
     Routed to the window that ASKED, so a share started in a call window does
     not open its chooser in the workspace. */
  useDesktopEvent('screen-source-request', (event) => {
    void requestScreenSource(event.requestId);
  });

  return (
    <>
      {isDesktopCallWindow() ? <CallWindowRuntime /> : <WorkspaceShellRuntime />}
      {isDesktopShell() ? <ScreenSourcePicker /> : null}
    </>
  );
}

/**
 * The half that belongs to the workspace window — and, in a browser, to the
 * only window there is, which is why the browser path is unchanged.
 */
function WorkspaceShellRuntime() {
  useDesktopNavigation();
  useDesktopCalls();
  useDesktopPresence();
  /* PRESENCE COMES BACK FROM THE OTHER WINDOWS. This document owns the one
     idle clock a person gets, so it has to be told about the call running in a
     window it cannot see — otherwise it receives no input for the whole call,
     goes Away five minutes in, and paints THIS USER'S OWN chip and tray with
     it. A local mirror, not a presence input: the roster is served from
     `activity_state`, which the server derives from `call_participants`. */
  useDesktopCallPresence();
  useDesktopUpdates();
  return null;
}

/**
 * The half that belongs to a CALL window.
 *
 * One hook, and it is the other end of the bus above: this document is the only
 * one that knows there is a call, and the only one that knows when the user
 * starts sharing their screen from it.
 */
function CallWindowRuntime() {
  useDesktopCallActivity();
  return null;
}
